import { explainCode, isKnownCode, type DiagnosticSeverity } from 'src/lib/diagnostics';

const SEVERITY_LABEL = {
  blocks: 'Bloquea',
  warns: 'Advertencia',
  info: 'Nota',
} satisfies Record<DiagnosticSeverity, string>;

type DiagnosticListProps = {
  codes: string[];
  /** Shown above the list when there is at least one code. */
  title?: string;
  /** `compact` drops the fix line — for dense rows where the full anatomy would drown the content. */
  variant?: 'full' | 'compact';
};

/**
 * The only sanctioned way to put a machine code on screen.
 *
 * A reader of this dashboard is an editor, not an engineer. `E006_EVIDENCE_MISMATCH` tells them
 * nothing and, worse, looks like something they broke. Every code here arrives with what it means
 * and what would change it, and the raw token stays available for the bug report.
 */
export function DiagnosticList({ codes, title, variant = 'full' }: DiagnosticListProps) {
  if (codes.length === 0) return null;

  return (
    <section className="diagnostics">
      {title && <h4 className="diagnostics__title">{title}</h4>}
      <ul className="diagnostics__list">
        {codes.map(code => {
          const diagnostic = explainCode(code);
          return (
            <li key={code} className="diagnostic" data-severity={diagnostic.severity}>
              <p className="diagnostic__head">
                <span className="diagnostic__severity">{SEVERITY_LABEL[diagnostic.severity]}</span>
                <span className="diagnostic__title">{diagnostic.title}</span>
                {!isKnownCode(code) && <span className="diagnostic__unknown">código no catalogado</span>}
              </p>
              <p className="diagnostic__what">{diagnostic.what}</p>
              {variant === 'full' && <p className="diagnostic__fix">{diagnostic.fix}</p>}
              {/* The raw code survives for the bug report, quiet and last. */}
              <p className="diagnostic__code mono">{code}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
